import React, { useMemo } from "react";
import {
  HiOutlineCash,
  HiOutlineClock,
  HiOutlineCreditCard,
  HiOutlineExclamation,
  HiOutlineShieldExclamation,
  HiOutlineTrendingUp,
} from "react-icons/hi";
import { PaCompactMetricChip } from "components/platform-admin";
import { buildFinancialMetricGroups } from "./billingOverview.utils";

const METRIC_ICONS = {
  collected_all_time: HiOutlineCash,
  outstanding: HiOutlineExclamation,
  collected_last_30_days: HiOutlineTrendingUp,
  estimated_mrr: HiOutlineTrendingUp,
  active_subscriptions: HiOutlineCreditCard,
  on_trial: HiOutlineClock,
  failed_payments: HiOutlineExclamation,
  wildcard_accounts: HiOutlineShieldExclamation,
  read_only_billing: HiOutlineShieldExclamation,
};

const ChipSkeleton = ({ className = "h-[72px]" }) => (
  <div className={`animate-pulse rounded-lg border border-gray-200 bg-gray-100 ${className}`} />
);

const MetricGroup = ({ title, children, gridClassName = "flex flex-wrap gap-2" }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</p>
    <div className={gridClassName}>{children}</div>
  </section>
);

const renderMetrics = (metrics, activeMetricId, fill) =>
  metrics.map((metric) => (
    <PaCompactMetricChip
      key={metric.id}
      {...metric}
      icon={METRIC_ICONS[metric.id]}
      isActive={activeMetricId === metric.id}
      fill={fill}
    />
  ));

const FinancialOverviewSummary = ({ cards = [], loading = false, activeMetricId = null }) => {
  const groups = useMemo(() => buildFinancialMetricGroups(cards), [cards]);
  const groupMap = useMemo(
    () => Object.fromEntries(groups.map((group) => [group.id, group])),
    [groups]
  );

  if (loading) {
    return (
      <div className="space-y-3">
        <MetricGroup title="Revenue snapshot" gridClassName="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <ChipSkeleton key={index} />
          ))}
        </MetricGroup>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <MetricGroup title="Tenant billing mix">
            {Array.from({ length: 3 }).map((_, index) => (
              <ChipSkeleton key={index} className="h-[52px] min-w-[7rem] flex-1" />
            ))}
          </MetricGroup>
          <MetricGroup title="Billing exceptions">
            {Array.from({ length: 2 }).map((_, index) => (
              <ChipSkeleton key={index} className="h-[52px] min-w-[7rem] flex-1" />
            ))}
          </MetricGroup>
        </div>
      </div>
    );
  }

  const revenueGroup = groupMap.revenue;
  const tenantsGroup = groupMap.tenants;
  const exceptionsGroup = groupMap.exceptions;

  return (
    <div id="metrics-revenue" className="scroll-mt-24 space-y-3">
      {revenueGroup ? (
        <MetricGroup
          title={revenueGroup.title}
          gridClassName="grid grid-cols-2 gap-2 lg:grid-cols-4"
        >
          {renderMetrics(revenueGroup.metrics, activeMetricId, false)}
        </MetricGroup>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {tenantsGroup ? (
          <MetricGroup title={tenantsGroup.title}>
            {renderMetrics(tenantsGroup.metrics, activeMetricId, true)}
          </MetricGroup>
        ) : null}
        {exceptionsGroup ? (
          <MetricGroup title={exceptionsGroup.title}>
            {renderMetrics(exceptionsGroup.metrics, activeMetricId, true)}
          </MetricGroup>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(FinancialOverviewSummary);
